import { isEmpty, isNil } from 'ramda';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { selectors, utils } from '@/Store';
import { getViewPage, getWatchPath } from '@/Utils/PageLocation';

import useSimpleModal from '@/Components/Modal/useSimpleModal';
import navigateToPackage, { getOfferIdFromViewingMethod } from '@/Utils/Puj';
import usePackageOfferDetails from './PUJ/usePackageOfferDetails';
import useDevice from './useDevice';
import usePujFlag from './usePujFlag';
import { useSubscriptionModal, useVodRequiredSubscriptions } from './useSubscription';

// TODO: use <Link> to watch page url directly instead of this hook
const useVideoPlayer = () => {
  const history = useHistory();
  const device = useDevice();
  const location = useLocation();
  const { isPUJEnabled } = usePujFlag();
  const { openModal } = useSimpleModal();

  const getMovie = useSelector(selectors.titles.getMovie);
  const getBrand = useSelector(selectors.titles.getBrand);
  const showUpgradeSubscriptionModal = useSubscriptionModal();
  const getRequiredSubscriptions = useVodRequiredSubscriptions();
  const { getEpisodeViewingMethods } = usePackageOfferDetails();
  const openAndPlay = useCallback(
    async (
      brandId: string,
      episodeId?: string,
      playbackOrigin?: string,
      ignoreEntitlementCheck?: boolean,
    ) => {
      // Device registration check
      if (device.isDeviceRegistrationLimitExceeded) {
        device.showDeviceRegistrationLimitModal();
        return;
      }

      // Entitlement subscription check
      const requiredSubscriptions = getRequiredSubscriptions(brandId, episodeId);

      if (
        !ignoreEntitlementCheck &&
        !isNil(requiredSubscriptions) &&
        !isEmpty(requiredSubscriptions)
      ) {
        let analyticsParams: any = {
          categoryName: playbackOrigin,
          viewName: getViewPage(history),
        };

        if (episodeId) {
          const brand = getBrand(brandId);
          analyticsParams = {
            ...analyticsParams,
            contentId: episodeId,
            contentTitle: brand?.title,
            contentType: utils.analytics.getContentTypeByBrand(brand),
            channelName: utils.analytics.getSegmentControllingChannel(brand),
          };
          if (isPUJEnabled) {
            const viewingMethods = await getEpisodeViewingMethods(episodeId);
            const offerId = getOfferIdFromViewingMethod(viewingMethods, 'VodViewingMethod');
            navigateToPackage({
              offerId,
              history,
              location,
              openModal,
            });
          }
        } else {
          const movie = getMovie(brandId);

          analyticsParams = {
            ...analyticsParams,
            contentId: brandId,
            contentTitle: movie?.title,
            contentType: utils.analytics.getContentTypeByBrand(movie),
            channelName: utils.analytics.getSegmentControllingChannel(movie),
          };
        }
        if (!isPUJEnabled) {
          showUpgradeSubscriptionModal(requiredSubscriptions, analyticsParams);
        }
        return;
      }

      const path = getWatchPath(history, brandId, episodeId, playbackOrigin);

      history.push(path);
    },
    [
      device,
      getRequiredSubscriptions,
      history,
      openModal,
      getMovie,
      getBrand,
      showUpgradeSubscriptionModal,
      isPUJEnabled,
      getEpisodeViewingMethods,
      location,
    ],
  );

  return { openAndPlay };
};

export default useVideoPlayer;
